// Account UI. Login is REQUIRED to use the app (see ui/authGate) — the same login form is shown
// as the gate's wall AND inside the app's account panel, via the shared buildLoginForm() below.
// Signed-IN, the account panel shows display name + tier + sign-out.

import { el } from './dom';
import { auth, type AuthState, type Tier } from '../auth/store';

const TIER_LABEL: Record<Tier, string> = { free: 'Free', premium: 'Premium', admin: 'Admin' };

// Brand glyphs for the OAuth buttons. Static markup (no user input) → innerHTML is safe.
const GOOGLE_GLYPH =
  '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">' +
  '<path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.55-5.17 3.55-8.87Z"/>' +
  '<path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.27v3.09A12 12 0 0 0 12 24Z"/>' +
  '<path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.38l4-3.09Z"/>' +
  '<path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.43-3.43C17.96 1.19 15.24 0 12 0A12 12 0 0 0 1.27 6.62l4 3.09C6.22 6.86 8.87 4.75 12 4.75Z"/>' +
  '</svg>';
const APPLE_GLYPH =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">' +
  '<path d="M17.05 12.94c-.03-2.7 2.2-3.99 2.3-4.06-1.25-1.84-3.2-2.09-3.9-2.12-1.66-.17-3.24.97-4.08.97-.84 0-2.14-.95-3.52-.92-1.81.03-3.48 1.05-4.41 2.67-1.88 3.27-.48 8.1 1.35 10.76.9 1.3 1.97 2.76 3.38 2.71 1.36-.05 1.87-.88 3.51-.88 1.64 0 2.1.88 3.53.85 1.46-.03 2.38-1.33 3.27-2.64 1.03-1.51 1.46-2.97 1.48-3.05-.03-.01-2.84-1.09-2.87-4.32M14.4 5.12c.74-.9 1.24-2.15 1.1-3.39-1.07.04-2.36.71-3.13 1.61-.69.79-1.29 2.06-1.13 3.27 1.19.09 2.42-.6 3.16-1.49"/>' +
  '</svg>';

/**
 * The shared sign-in / sign-up form. A segmented control switches between Sign in and Create account
 * (one primary action, not a wall of buttons); a magic link and Google/Apple sit below. Used by the
 * full-screen auth gate (the required wall) and by the in-app account panel. Self-contained — it
 * talks to the auth store directly; a successful sign-in flips the store, which the gate/panel
 * observe. `lead` is an optional one-line note under the tabs (the panel uses it for "session
 * expired"); the wall passes none.
 */
export function buildLoginForm(lead = 'Sign in or create an account.'): HTMLElement {
  let mode: 'in' | 'up' = 'in';

  const email = el('input', {
    class: 'ar-auth-input', type: 'email', placeholder: 'you@example.com',
    autocomplete: 'email', autocapitalize: 'off', spellcheck: false,
  }) as HTMLInputElement;
  const password = el('input', {
    class: 'ar-auth-input', type: 'password', placeholder: 'Password', autocomplete: 'current-password',
  }) as HTMLInputElement;
  const status = el('div', { class: 'ar-auth-status', style: { display: 'none' } });

  const controls: HTMLButtonElement[] = [];
  const setStatus = (msg: string | null, err = false): void => {
    status.textContent = msg ?? '';
    status.style.display = msg ? '' : 'none';
    status.classList.toggle('err', err);
  };
  // Run an async auth action with the form disabled + status feedback. A failure shows its message;
  // a success with a message (confirm email / magic link sent) shows it too. A plain success
  // (password sign-in) just lets the auth-state subscription flip the gate/panel.
  const run = async (label: string, fn: () => Promise<{ ok: boolean; message?: string }>): Promise<void> => {
    controls.forEach((b) => (b.disabled = true));
    setStatus(`${label}…`);
    try {
      const r = await fn();
      if (!r.ok) setStatus(r.message ?? 'Failed. Try again.', true);
      else if (r.message) setStatus(r.message, false);
      else setStatus(null);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Failed. Try again.', true);
    } finally {
      controls.forEach((b) => (b.disabled = false));
    }
  };

  const needEmail = (): string | null => {
    const v = email.value.trim();
    if (!v) { setStatus('Enter your email.', true); email.focus(); return null; }
    return v;
  };

  // ── mode toggle: one primary action, relabelled by the active tab ──
  const tabIn = el('button', { class: 'ar-authtab active', type: 'button', onclick: () => setMode('in') }, ['Sign in']) as HTMLButtonElement;
  const tabUp = el('button', { class: 'ar-authtab', type: 'button', onclick: () => setMode('up') }, ['Create account']) as HTMLButtonElement;

  const primaryLbl = el('span', {}, ['Sign in']);
  const primary = el('button', {
    class: 'ar-btn primary ar-auth-primary',
    onclick: () => {
      const e = needEmail();
      if (!e) return;
      if (mode === 'in') {
        if (!password.value) { setStatus('Enter your password, or use the sign-in link.', true); password.focus(); return; }
        void run('Signing in', () => auth.signInWithEmail(e, password.value));
      } else {
        if (password.value.length < 6) { setStatus('Choose a password of at least 6 characters.', true); password.focus(); return; }
        void run('Creating account', () => auth.signUpWithEmail(e, password.value));
      }
    },
  }, [primaryLbl]) as HTMLButtonElement;

  function setMode(next: 'in' | 'up'): void {
    mode = next;
    tabIn.classList.toggle('active', next === 'in');
    tabUp.classList.toggle('active', next === 'up');
    primaryLbl.textContent = next === 'in' ? 'Sign in' : 'Create account';
    password.autocomplete = next === 'in' ? 'current-password' : 'new-password';
    password.placeholder = next === 'in' ? 'Password' : 'Choose a password';
    setStatus(null);
  }

  // Magic link — a quiet text link, works in either mode (passwordless).
  const magic = el('button', {
    class: 'ar-auth-altlink', type: 'button',
    onclick: () => { const e = needEmail(); if (e) void run('Sending link', () => auth.signInWithMagicLink(e)); },
  }, ['Email me a sign-in link instead']) as HTMLButtonElement;

  const oauthBtn = (provider: 'google' | 'apple', glyph: string, label: string): HTMLButtonElement => {
    const ico = el('span', { class: 'ar-oauth-ico' });
    ico.innerHTML = glyph;
    return el('button', {
      class: 'ar-oauth-btn', type: 'button',
      onclick: () => void run(`Opening ${label}`, () => auth.signInWithOAuth(provider)),
    }, [ico, label]) as HTMLButtonElement;
  };
  const google = oauthBtn('google', GOOGLE_GLYPH, 'Google');
  const apple = oauthBtn('apple', APPLE_GLYPH, 'Apple');

  controls.push(primary, magic, google, apple);

  // Enter in either field submits the active mode's primary action.
  const onEnter = (ev: KeyboardEvent): void => { if (ev.key === 'Enter') primary.click(); };
  email.addEventListener('keydown', onEnter);
  password.addEventListener('keydown', onEnter);

  return el('div', { class: 'ar-login' }, [
    el('div', { class: 'ar-authtabs', role: 'tablist' }, [tabIn, tabUp]),
    lead ? el('p', { class: 'ar-auth-lead' }, [lead]) : null,
    el('label', { class: 'ar-auth-field' }, [el('span', {}, ['Email']), email]),
    el('label', { class: 'ar-auth-field' }, [el('span', {}, ['Password']), password]),
    status,
    primary,
    magic,
    el('div', { class: 'ar-or' }, ['or continue with']),
    el('div', { class: 'ar-oauth-row' }, [google, apple]),
  ]);
}

/** The in-app account panel — content for the Account submenu (a settings screen like every other
 *  menu section, NOT a modal). Reachable from the menu once you're in the app (so always signed in);
 *  shows identity + tier + sign-out, and re-renders in place on any auth-state change. */
export class AccountPanel {
  readonly content: HTMLElement;

  constructor() {
    this.content = el('div', { class: 'ar-account-content' });
    this.render();
    // Re-render in place whenever auth state changes — the submenu may be off-screen; that's fine,
    // it's cheap and the screen is correct the moment it's opened.
    auth.subscribe(() => this.render());
  }

  private render(): void {
    this.content.innerHTML = '';
    const s = auth.snapshot;
    if (s.user) this.renderSignedIn(s);
    else this.content.append(buildLoginForm()); // session expired in-app → offer to sign back in
  }

  // ── signed-in: identity + tier + sign out ──
  private renderSignedIn(s: AuthState): void {
    const name = s.profile?.display_name?.trim() || s.profile?.username?.trim() || s.user?.email || 'Signed in';
    const tier = s.tier;

    const signOut = el('button', {
      class: 'ar-btn danger',
      onclick: () => { signOut.disabled = true; void auth.signOut(); },
    }, ['Sign out']) as HTMLButtonElement;

    const boat = s.profile?.boat_name?.trim();
    // el() filters out `false` children, but the native body.append() does not — so build the
    // list here and drop the falsy entries before appending.
    const children: Array<HTMLElement | null> = [
      el('div', { class: 'ar-auth-me' }, [
        el('div', { class: 'ar-auth-id' }, [
          el('div', { class: 'ar-auth-name' }, [name]),
          s.user?.email && name !== s.user.email ? el('div', { class: 'ar-auth-sub' }, [s.user.email]) : false,
        ]),
        el('div', { class: 'ar-auth-tier ' + tier, title: `Account tier: ${TIER_LABEL[tier]}` }, [TIER_LABEL[tier]]),
      ]),
      boat ? el('div', { class: 'ar-auth-boat' }, ['⛵ ' + boat]) : null,
      tier === 'free' ? el('div', { class: 'ar-modal-note', style: { padding: '0 0 12px' } }, ['Free tier: base chart, seamarks and tides. Premium adds the depth charts and anchoring heat-map.']) : null,
      el('div', { class: 'ar-modal-actions' }, [signOut]),
    ];
    this.content.append(...children.filter((n): n is HTMLElement => n !== null));
  }
}

/**
 * The account entry point: a row that lives INSIDE the settings menu (not persistent chrome). It
 * navigates to the Account submenu (a settings screen like every other section — no modal) and
 * reflects the signed-in identity + tier. Mount the row via `.button` (into the menu's account row
 * slot) and the screen content via `.panel.content` (into the account submenu body).
 */
export class AccountControl {
  readonly panel = new AccountPanel();
  readonly button: HTMLButtonElement;
  private nameEl: HTMLElement;
  private subEl: HTMLElement;

  /** @param onOpen called when the row is tapped — the panel navigates to its Account submenu. */
  constructor(onOpen: () => void) {
    this.nameEl = el('div', { class: 'ar-account-row-name' }, ['Account']);
    this.subEl = el('div', { class: 'ar-account-row-sub' }, ['Profile, tier & sign out']);
    this.button = el('button', {
      class: 'ar-account-row', title: 'Account', onclick: onOpen,
    }, [
      el('span', { class: 'ar-account-row-ico' }, ['👤']),
      el('div', { class: 'ar-account-row-txt' }, [this.nameEl, this.subEl]),
      el('span', { class: 'ar-account-row-chev' }, ['›']),
    ]) as HTMLButtonElement;
    auth.subscribe((s) => this.update(s));
  }

  private update(s: AuthState): void {
    this.nameEl.textContent = s.user ? (s.profile?.display_name?.trim() || s.user.email || 'Account') : 'Account';
    this.subEl.textContent = s.user ? `${TIER_LABEL[s.tier]} account · profile & sign out` : 'Sign in';
    // accent the avatar tile for an active premium/admin tier at a glance
    this.button.classList.toggle('premium', s.isPremium);
  }
}
